import {
  EyeInvisibleOutlined,
  EyeOutlined,
  FundProjectionScreenOutlined,
  OrderedListOutlined,
  ProfileOutlined,
  UnorderedListOutlined,
} from '@ant-design/icons';
import { Alert, Button, Card, Descriptions, Flex, Spin, Tooltip, Typography } from 'antd';
import PropTypes from 'prop-types';
import { useState } from 'react';
import ProfitReportDrilldown from './ProfitReportDrilldown';
import {
  FACT_REPORT_SORTS,
  getFactReportDescriptionItems,
  getFactReportDrilldownContextKey,
  getFactReportMetricActionType,
  getFactReportMetricDisplayName,
  getFactReportMetricDisplayValue,
  getSectionControl,
  getFactReportVisibleSections,
  getNextFactReportSort,
} from '../utils/profit-report';

const renderMetricStageValue = (item, displayValue) =>
  item.value?.stage === 'forecast' ? (
    <Tooltip title="推测值">
      <Typography.Text type="warning">
        <FundProjectionScreenOutlined aria-label="推测值" /> {displayValue}
      </Typography.Text>
    </Tooltip>
  ) : (
    displayValue
  );

const getSectionTitle = (section) => {
  const summaryItems = section?.summaryItems || [];
  if (!summaryItems.length) {
    return section.title;
  }
  return (
    <Flex gap="middle" wrap>
      {summaryItems.map((item) => (
        <Flex key={item.key} gap="small">
          <Typography.Text>{getFactReportMetricDisplayName(item.name)}：</Typography.Text>
          {renderMetricStageValue(item, getFactReportMetricDisplayValue(item))}
        </Flex>
      ))}
    </Flex>
  );
};

const descriptionCellStyles = {
  content: { textAlign: 'right', width: '12.5%' },
  label: { width: '12.5%' },
};

const metricActionButtonStyles = {
  root: {
    paddingLeft: 0,
    paddingRight: 0,
    borderTop: 0,
    borderRight: 0,
    borderLeft: 0,
    borderRadius: 0,
  },
};

const SORT_LABELS = {
  [FACT_REPORT_SORTS.amountDesc]: '数值降序',
  [FACT_REPORT_SORTS.configOrder]: '配置顺序',
  [FACT_REPORT_SORTS.nameAsc]: '名称升序',
};

const SORT_ICONS = {
  [FACT_REPORT_SORTS.amountDesc]: <OrderedListOutlined />,
  [FACT_REPORT_SORTS.configOrder]: <ProfileOutlined />,
  [FACT_REPORT_SORTS.nameAsc]: <UnorderedListOutlined />,
};

const getSectionControlKey = (section, index = 0) =>
  section?.key || section?.title || `section-${index}`;

const getDefaultSectionControl = (section = {}) => ({
  hideZeroAmount: Boolean(section.summaryItems?.length),
  sortMode: section.summaryItems?.length
    ? FACT_REPORT_SORTS.amountDesc
    : FACT_REPORT_SORTS.configOrder,
});

const getInteractiveSectionControl = (section, index, controls = {}) => {
  const defaults = getDefaultSectionControl(section);
  return getSectionControl(section, index, {
    ...defaults,
    sectionControls: controls,
  });
};

const ProfitReportSortButton = ({ ariaLabel = undefined, onClick, sortMode }) => {
  const sortLabel = SORT_LABELS[sortMode];
  return (
    <Tooltip title={sortLabel}>
      <Button
        aria-label={ariaLabel || sortLabel}
        icon={SORT_ICONS[sortMode]}
        type="text"
        onClick={onClick}
      />
    </Tooltip>
  );
};

ProfitReportSortButton.propTypes = {
  ariaLabel: PropTypes.string,
  onClick: PropTypes.func.isRequired,
  sortMode: PropTypes.string.isRequired,
};

const ProfitReportSectionControls = ({ control, onToggleHideZeroAmount, onToggleSortMode }) => {
  const zeroAmountLabel = control.hideZeroAmount ? '隐藏数值0' : '显示数值0';
  return (
    <>
      <Tooltip title={zeroAmountLabel}>
        <Button
          aria-label={zeroAmountLabel}
          icon={control.hideZeroAmount ? <EyeInvisibleOutlined /> : <EyeOutlined />}
          type="text"
          onClick={onToggleHideZeroAmount}
        />
      </Tooltip>
      <ProfitReportSortButton sortMode={control.sortMode} onClick={onToggleSortMode} />
    </>
  );
};

ProfitReportSectionControls.propTypes = {
  control: PropTypes.shape({
    hideZeroAmount: PropTypes.bool,
    sortMode: PropTypes.string,
  }).isRequired,
  onToggleHideZeroAmount: PropTypes.func.isRequired,
  onToggleSortMode: PropTypes.func.isRequired,
};

const StoreProfitReport = ({
  data,
  debug,
  errorMessage,
  identityKey,
  loading,
  organizationId,
  onSectionControlsChange,
  reportParams,
  role,
  sectionControls,
}) => {
  const [drilldownMetric, setDrilldownMetric] = useState(null);
  const { sections = [] } = data;

  const toggleSectionHideZeroAmount = (section, index) => {
    const sectionKey = getSectionControlKey(section, index);
    const currentControl = getInteractiveSectionControl(section, index, sectionControls);
    onSectionControlsChange({
      ...sectionControls,
      [sectionKey]: {
        ...currentControl,
        hideZeroAmount: !currentControl.hideZeroAmount,
      },
    });
  };

  const toggleSectionSortMode = (section, index) => {
    const sectionKey = getSectionControlKey(section, index);
    const currentControl = getInteractiveSectionControl(section, index, sectionControls);
    onSectionControlsChange({
      ...sectionControls,
      [sectionKey]: {
        ...currentControl,
        sortMode: getNextFactReportSort(currentControl.sortMode),
      },
    });
  };

  const renderMetricValue = (item, displayValue) => {
    const actionType = getFactReportMetricActionType(item, role);
    if (actionType === 'none') {
      return renderMetricStageValue(item, displayValue);
    }
    const displayName = getFactReportMetricDisplayName(item.name);
    return (
      <Button
        aria-label={`查看${displayName}指标解释`}
        color="default"
        size="small"
        styles={metricActionButtonStyles}
        variant="dashed"
        onClick={() => {
          setDrilldownMetric(item);
        }}
      >
        {renderMetricStageValue(item, displayValue)}
      </Button>
    );
  };

  const sectionDisplayStates = (sections || []).flatMap((section, index) => {
    const sectionControl = getInteractiveSectionControl(section, index, sectionControls);
    return getFactReportVisibleSections([section], sectionControl).length
      ? [{ section, index, sectionControl }]
      : [];
  });

  const reportContent = (
    <>
      <Flex vertical gap="middle">
        {sectionDisplayStates.map(({ section, index, sectionControl }) => (
          <Card
            key={section.key}
            title={getSectionTitle(section)}
            extra={
              <ProfitReportSectionControls
                control={sectionControl}
                onToggleHideZeroAmount={() => toggleSectionHideZeroAmount(section, index)}
                onToggleSortMode={() => toggleSectionSortMode(section, index)}
              />
            }
            variant="borderless"
          >
            {(() => {
              const descriptionItems = getFactReportDescriptionItems(section, {
                ...sectionControl,
                renderValue: renderMetricValue,
              });
              return descriptionItems.length ? (
                <Descriptions
                  bordered
                  column={{ xs: 1, sm: 2, md: 4 }}
                  items={descriptionItems}
                  size="small"
                  styles={descriptionCellStyles}
                />
              ) : null;
            })()}
          </Card>
        ))}
      </Flex>
      <ProfitReportDrilldown
        debug={debug}
        key={getFactReportDrilldownContextKey(organizationId, reportParams, drilldownMetric)}
        identityKey={identityKey}
        metric={drilldownMetric}
        open={Boolean(drilldownMetric)}
        organizationId={organizationId}
        reportParams={reportParams}
        role={role}
        onClose={() => {
          setDrilldownMetric(null);
        }}
      />
    </>
  );
  const content = errorMessage ? (
    <Alert showIcon title={errorMessage} type="error" />
  ) : (
    reportContent
  );

  return <Spin spinning={loading}>{content}</Spin>;
};

StoreProfitReport.propTypes = {
  data: PropTypes.shape({
    sections: PropTypes.arrayOf(
      PropTypes.shape({
        key: PropTypes.string,
        title: PropTypes.node,
      }),
    ),
  }).isRequired,
  debug: PropTypes.bool.isRequired,
  errorMessage: PropTypes.string,
  identityKey: PropTypes.string.isRequired,
  loading: PropTypes.bool.isRequired,
  organizationId: PropTypes.string.isRequired,
  onSectionControlsChange: PropTypes.func.isRequired,
  reportParams: PropTypes.shape({
    endDate: PropTypes.string.isRequired,
    scope: PropTypes.shape({ type: PropTypes.string.isRequired }).isRequired,
    startDate: PropTypes.string.isRequired,
  }).isRequired,
  role: PropTypes.string,
  sectionControls: PropTypes.objectOf(
    PropTypes.shape({
      hideZeroAmount: PropTypes.bool.isRequired,
      sortMode: PropTypes.string.isRequired,
    }),
  ).isRequired,
};

export default StoreProfitReport;
